import { zodResolver } from '@hookform/resolvers/zod';
import { ReactElement } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { ReportRsSchema, ReportRsFormData } from './validationSchema';
import { Button, Dropdown, InputForm } from '@jobcost-monorepo/ui';
import { useTranslation } from '@jobcost-monorepo/i18n';
import { KeyValue } from '@jobcost-monorepo/utils';
import { getYearsKeyValues } from '@jobcost-monorepo/crud/utils/helpers/helperKeyValues';
import dayjs from '@jobcost-monorepo/dayjs-config';

interface IProps {
  handleGenerate: (year: number, percentage: number, projectName: string) => void;
  isLoading: boolean;
}

const ReportRsHeader = ({ handleGenerate, isLoading }: IProps): ReactElement => {
  const { t } = useTranslation(['report-rs', 'common']);

  const currentYear = dayjs().year();
  const years: KeyValue[] = getYearsKeyValues();

  const defaultValues: ReportRsFormData = {
    year: currentYear,
    percentage: 50,
    projectName: 'Nurale - R&S',
  };

  const onSubmit: SubmitHandler<ReportRsFormData> = (data) => {
    handleGenerate(data.year, data.percentage, data.projectName);
  };

  const methods = useForm<ReportRsFormData>({
    mode: 'onSubmit',
    values: defaultValues,
    defaultValues: JSON.parse(JSON.stringify(defaultValues)),
    resolver: zodResolver(ReportRsSchema),
  });

  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div className="flex overflow-y-auto max-h-32 flex-wrap gap-2 items-center">
          <Dropdown
            name="year"
            errorName="year"
            label={t('filter.year')}
            items={years}
            searchable
            width="w-24"
          />

          <InputForm
            name="percentage"
            errorName="percentage"
            label={t('filter.percentage')}
            type="number"
            width="w-24"
          />

          <InputForm
            name="projectName"
            errorName="projectName"
            label={t('filter.projectName')}
            width="w-72"
          />

          <Button type="submit" disabled={isLoading}>
            {isLoading ? t('filter.generating') : t('filter.generate')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default ReportRsHeader;
